import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const FIELDS = [
  { name: "project_name", label: "Project name", placeholder: "e.g. Mounting Bracket for HVAC Vibration Isolator", required: true },
  { name: "description", label: "Describe what you want to create", type: "textarea", placeholder: "Plain language — what is it, and what does it need to do?", required: true },
  { name: "intended_use", label: "Intended use", placeholder: "Where and how will it be used?" },
  { name: "primary_user", label: "Primary user", placeholder: "Who will handle or operate it?" },
  { name: "operating_environment", label: "Operating environment", placeholder: "Indoor/outdoor, temperature, humidity, chemicals, vibration…" },
  { name: "required_load", label: "Required load or performance", placeholder: "e.g. 500 N static load, 10 Hz cyclic" },
  { name: "approximate_dimensions", label: "Approximate dimensions", placeholder: "e.g. 120 × 80 × 20 mm" },
  { name: "preferred_material", label: "Preferred material", placeholder: "e.g. Aluminum 6061-T6, stainless 304" },
  { name: "manufacturing_method", label: "Manufacturing method", placeholder: "CNC, casting, sheet metal, injection molding…" },
  { name: "budget_priority", label: "Budget priority", placeholder: "Cost-first, balanced, precision-first…" },
  { name: "additional_constraints", label: "Additional constraints", type: "textarea", placeholder: "Standards, deadlines, interface fits, weight limits…" },
];

const empty = Object.fromEntries(FIELDS.map((f) => [f.name, ""]));

export default function Intake() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const set = (name, v) => setForm((s) => ({ ...s, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_name.trim() || !form.description.trim()) {
      toast.error("Project name and description are required.");
      return;
    }
    setSubmitting(true);
    try {
      const entitlementId = localStorage.getItem("aiude_entitlement_id");
      const headers = entitlementId ? { "X-Entitlement-Id": entitlementId } : {};
      const { data } = await api.post("/projects/intake", form, { headers });
      toast.success("Structured specification generated");
      nav(`/projects/${data.id}/specification`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to generate specification. Please try again.";
      if (String(msg).toLowerCase().includes("entitlement")) {
        toast.error("Please verify your email first.");
        nav("/start");
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-10">
      <div>
        <div className="font-tech mb-2">Stage 01 — Intent Capture</div>
        <h1 className="text-3xl lg:text-4xl font-semibold text-white">Describe the product or component</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-sm">
          Provide as much or as little as you know. A.I.U.D.E. will translate ordinary language into
          a structured engineering specification you can then edit and approve.
        </p>
      </div>

      <WorkflowDiagram activeId="intent" compact />

      <form onSubmit={submit} className="panel-elev p-6 lg:p-10 grid md:grid-cols-2 gap-5" data-testid="intake-form">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <label className="label-cad" htmlFor={f.name}>{f.label}{f.required && <span className="text-[#00D2FF]"> *</span>}</label>
            {f.type === "textarea" ? (
              <textarea
                id={f.name}
                rows={4}
                data-testid={`intake-${f.name}`}
                value={form[f.name]}
                onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder}
                className="input-cad resize-none"
              />
            ) : (
              <input
                id={f.name}
                data-testid={`intake-${f.name}`}
                value={form[f.name]}
                onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder}
                className="input-cad"
              />
            )}
          </div>
        ))}
        <div className="md:col-span-2 flex flex-wrap items-center gap-3 mt-2 justify-between">
          <button type="button" className="btn-ghost text-sm" onClick={() => nav(-1)} data-testid="intake-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button type="submit" className="btn-primary" disabled={submitting} data-testid="intake-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating specification…</> : <>Generate structured specification <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </form>
    </div>
  );
}
